import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogProps,
  IconButton,
} from "@mui/material";
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
} from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { Button, ButtonProps } from "../../Inputs/Button/Button";

export type SrDialogProps = Readonly<
  PropsWithChildren<
    Omit<DialogProps, "onClose"> & {
      name: string;
      closeButtonAriaLabel?: string;
      withCloseButton?: boolean;
      onClose?(): void;
    }
  >
>;

type SrDialogContext = Readonly<{
  name: string;
  closeButtonAriaLabel: string;
  titleId: string;
  descriptionId: string;
  withCloseButton: boolean;
  onClose?(): void;
}>;

const Context = createContext<SrDialogContext | null>(null);
function useDialogContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useDialogContext must be used within a SrDialog component");
  }
  return context;
}

export function SrDialog({
  closeButtonAriaLabel = "Close",
  name,
  open,
  onClose,
  maxWidth = "sm",
  withCloseButton = false,
  children,
  ...props
}: SrDialogProps): ReactElement {
  const id = `${name}-Dialog`;
  const titleId = `${id}-Title`;
  const descriptionId = `${id}-Description`;

  const contextValue: SrDialogContext = useMemo(
    () => ({
      name,
      closeButtonAriaLabel,
      titleId,
      descriptionId,
      onClose,
      withCloseButton,
    }),
    [name, closeButtonAriaLabel, titleId, descriptionId, onClose, withCloseButton],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      id={id}
      data-testid={id}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      maxWidth={maxWidth}
      {...props}
    >
      <Context.Provider value={contextValue}>{children}</Context.Provider>
    </Dialog>
  );
}

SrDialog.Title = SrDialogTitle;
SrDialog.Content = SrDialogContent;
SrDialog.Actions = SrDialogActions;
SrDialog.Action = SrDialogAction;

type SrDialogTitleProps = Readonly<
  PropsWithChildren<{
    onClose?(): void;
  }>
>;

function SrDialogTitle({ onClose: onCloseProp, children }: SrDialogTitleProps) {
  const { closeButtonAriaLabel, titleId, withCloseButton, onClose } = useDialogContext();
  const handleClose = onCloseProp || onClose;

  return (
    <DialogTitle
      id={titleId}
      component="div"
      sx={[
        { py: 3 },
        !!withCloseButton && {
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          gap: 2,
        },
      ]}
    >
      {children}
      {withCloseButton ? (
        <IconButton aria-label={closeButtonAriaLabel} color="inherit" onClick={handleClose}>
          <FaIcon icon={faXmark} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

type SrDialogContentProps = Readonly<PropsWithChildren>;

function SrDialogContent({ children }: SrDialogContentProps) {
  const { descriptionId } = useDialogContext();
  const isTextContent = ["string", "number"].includes(typeof children);

  return (
    <DialogContent id={!isTextContent ? descriptionId : undefined} sx={{ pb: 3 }}>
      {isTextContent ? (
        <DialogContentText id={descriptionId}>{children}</DialogContentText>
      ) : (
        children
      )}
    </DialogContent>
  );
}

type SrDialogActionsProps = Readonly<
  PropsWithChildren<{
    /**
     * Leading slot opposite the actions, for a footer-level status line
     * ("3 of 5 selected") or error. A leading child alone cannot produce this
     * layout: `DialogActions` right-aligns its children and this component
     * exposes no `sx`, so `space-between` would also push the actions apart.
     */
    hint?: ReactNode;
  }>
>;

function SrDialogActions({ hint, children }: SrDialogActionsProps) {
  if (hint == null) {
    return <DialogActions sx={{ pt: { sm: 1 } }}>{children}</DialogActions>;
  }

  return (
    <DialogActions sx={{ pt: { sm: 1 }, justifyContent: "space-between" }}>
      <Box sx={{ minWidth: 0 }}>{hint}</Box>
      {/* Wrapping the actions takes them out of reach of the `DialogActions`
          sibling-margin rule, so restore that spacing here. */}
      <Box sx={{ display: "flex", alignItems: "center", flex: "none", gap: 1 }}>{children}</Box>
    </DialogActions>
  );
}

type SrDialogActionProps = Readonly<
  PropsWithChildren<
    Omit<ButtonProps, "name"> & {
      name: string;
    }
  >
>;

function SrDialogAction({ name, children, ...props }: SrDialogActionProps) {
  const { name: dialogName } = useDialogContext();

  return (
    <Button
      data-testid={`${dialogName}-Dialog-Action-${name}`}
      size="large"
      variant="contained"
      {...props}
    >
      {children}
    </Button>
  );
}
